import { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import {
  type DeleteErrorInfo,
  type MasterCrudRow,
  type ModalState,
} from '~/pages/AdminOcctoMaster/shell/types'
import {
  isRetriableDeleteError,
  mapMasterDeleteError,
} from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'

/**
 * `useDelete<Master>` mutation hook の返り値 (構造的 subset)。connect-query の
 * `UseMutationResult<Response, Error, MessageInitShape<Req>>` を受けるため、
 * mutateAsync の引数型は any にする (proto message ブランドの contravariance が
 * generic では吸収できない)。呼び出し側は id + expectedUpdatedAt しか渡さない。
 */
type DeleteMutation = {
  mutateAsync: (req: {
    id: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- proto Timestamp ブランドを generic で吸収できないため
    expectedUpdatedAt: any
  }) => Promise<unknown>
  isPending: boolean
}

type Args<Row extends MasterCrudRow> = {
  modalState: ModalState<Row>
  closeModal: () => void
  setDeleteError: (err: DeleteErrorInfo | undefined) => void
  /** `page:adminOcctoMaster.<master>.serverError` (mapMasterDeleteError の prefix)。 */
  errorKeyPrefix: string
  /**
   * `useDelete<Master>` 相当の mutation hook (`useInvalidatingMutation` 経由)。
   * 全 3 タブ (BG / 事業者 / 発電所) で request 型は `{id, expectedUpdatedAt}` に一致する。
   */
  useDelete: () => DeleteMutation
}

/**
 * BgMaster / BusinessMaster / PlantMaster が 3 回写経していた inline 削除確定処理を
 * 集約する。 dialog 内部で mutation を持つ他 5 タブは呼ばない。
 *
 * 呼び出し側は `useMasterCrudTab` から modalState / closeModal / setDeleteError を
 * 受けて本 hook に流し、返る `handleDeleteConfirm` を DeleteConfirmDialog の
 * onConfirm に渡す。
 */
export function useMasterInlineDelete<Row extends MasterCrudRow>(
  args: Args<Row>,
): { handleDeleteConfirm: () => Promise<void>; isDeleting: boolean } {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { modalState, closeModal, setDeleteError, errorKeyPrefix, useDelete } =
    args
  const { mutateAsync, isPending } = useDelete()

  const handleDeleteConfirm = useCallback(async (): Promise<void> => {
    if (modalState.kind !== 'delete') {
      return
    }
    const { row } = modalState
    try {
      await mutateAsync({
        id: row.id,
        expectedUpdatedAt: row.updatedAt,
      })
      closeModal()
      // 削除成功の toast (2026-08-28 デザイナー指摘「削除・追加・編集の成功時は
      // 全画面共通で toast を出す」)。 この hook を通る 3 タブ (BG / 事業者 /
      // 発電所) がまとめて満たされる。
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      setDeleteError({
        message: mapMasterDeleteError(t, err, errorKeyPrefix),
        retriable: isRetriableDeleteError(err),
      })
    }
  }, [
    modalState,
    mutateAsync,
    closeModal,
    setDeleteError,
    errorKeyPrefix,
    showSuccessToast,
    t,
  ])

  return { handleDeleteConfirm, isDeleting: isPending }
}
